<template>
  <div class="px-4 pt-6">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">Packaging Label Print</h1>

    <!-- Instructions -->
    <details class="bg-blue-100 border-l-4 border-blue-500 text-blue-900 p-4 rounded-lg shadow-md dark:bg-gray-800 mb-4">
      <summary class="cursor-pointer select-none text-lg font-semibold dark:text-white">
        Instructions (click to expand)
      </summary>
      <div class="mt-3">
        <ol class="list-decimal list-inside text-base font-normal text-gray-800 dark:text-gray-300 space-y-1">
          <li>
            Install <strong>QZ Tray</strong> from
            <a href="https://qz.io/download/" class="text-blue-600 hover:underline" target="_blank" rel="noopener">qz.io/download</a>
          </li>
          <li>
            Setup certificates using the provided script:
            <ul class="list-disc list-inside ml-6 space-y-1">
              <li>
                <a href="/docs/qz-cert-setup-script/" class="text-blue-600 hover:underline" target="_blank" rel="noopener">
                  Download installer ZIP
                </a>
              </li>
              <li>Unpack and run <code>windows-install.bat</code> with Administrator rights.</li>
              <li>Restart QZ Tray afterwards.</li>
              <li>Verify in QZ Tray → Advanced → Site Manager that the certificate is installed.</li>
            </ul>
          </li>
          <li>Enter quantity, create serials, then use the controls below to select a printer and print labels.</li>
        </ol>
      </div>
    </details>

    <!-- Serial Creation -->
    <div class="p-4 bg-white border mt-4 border-gray-200 rounded-lg shadow-sm dark:border-gray-700 sm:p-6 dark:bg-gray-800">
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Packaging Labels</h3>
        
        <!-- Important Notice -->
        <div class="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-md dark:bg-yellow-900/20 dark:border-yellow-500">
          <div class="flex">
              <div class="ml-3">
                <p class="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Important:</strong> Create and print in manageable batches (recommended: 1-200 labels). 
                  <strong>If you reload the page, all created serials will be lost</strong> and you'll need to create them again.
                </p>
                <p class="text-sm text-yellow-800 dark:text-yellow-200 mt-1">
                  <strong>重要提示：</strong> 请分批创建和打印（建议：1-200个标签）。
                  <strong>如果重新加载页面，所有已创建的序列号将丢失</strong>，需要重新创建。
                </p>
              </div>
          </div>
        </div>
        
        <div class="flex items-center space-x-4">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-200">Quantity:</label>
            <input type="number" v-model.number="quantity" min="1" max="200" class="px-2 py-1 w-28 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
            <button type="button" class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 text-base font-medium" @click="createAndLoadSerials" :disabled="isLoading">
                {{ isLoading ? 'Creating...' : 'Create & Load Serials' }}
            </button>
        </div>
    </div>
    
    <!-- QZ Tray Connection Bar -->
    <div class="p-4 bg-white border mt-4 border-gray-200 rounded-lg shadow-sm dark:border-gray-700 sm:p-6 dark:bg-gray-800">
      <div class="flex items-center justify-between">
        <button :class="connectBtnClass" @click="toggleQz">
          {{ qzConnected ? 'Disconnect' : 'Connect' }}
        </button>
        <div class="space-x-2">
          <button id="refreshPrintersBtn" class="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium" @click="refreshPrinters">
            Refresh Printers
          </button>
        </div>
      </div>

      <!-- Printer Name Selection -->
      <div class="flex items-center space-x-4 mt-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-200">Printer Name:</label>
        <select v-model="printerName" @change="autoSetDpiFromPrinter" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          <option v-for="p in printers" :key="p" :value="p">{{ p }}</option>
        </select>
      </div>

      <!-- DPI Selection -->
      <div class="flex items-center space-x-4 mt-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-200">DPI:</label>
        <select v-model="dpi" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          <option value="203">203</option>
          <option value="300">300</option>
        </select>
      </div>

      <!-- ZPL Adjustments -->
      <div class="flex items-center space-x-6 mt-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-200">Adjustments:</label>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-700 dark:text-gray-200">^LS (left/right)</span>
          <input type="number" v-model.number="offsetLS" min="-120" max="120" step="1" class="px-2 py-1 w-28 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-700 dark:text-gray-200">^LT (up/down)</span>
          <input type="number" v-model.number="offsetLT" min="-120" max="120" step="1" class="px-2 py-1 w-28 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        </div>
      </div>

      <!-- Messages -->
      <div id="messageDisplay" class="p-4 bg-gray-100 border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 mt-4">
        <h4 class="mb-2 text-lg font-bold text-gray-900 dark:text-white">Messages:</h4>
        <div class="text-gray-700 dark:text-gray-300 space-y-1 max-h-64 overflow-y-auto">
          <div v-for="(m, i) in messages" :key="i">{{ m }}</div>
        </div>
      </div>
    </div>

    <!-- Serials Table -->
    <div v-if="serials.length" class="p-4 bg-white border mt-4 border-gray-200 rounded-lg shadow-sm dark:border-gray-700 sm:p-6 dark:bg-gray-800">
      <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Serials for Printing</h3>

      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center space-x-4">
          <button type="button" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:ring-2 focus:ring-gray-400" @click="toggleSelectAll">
            {{ allSelected ? 'Unselect All' : 'Select All' }}
          </button>
        </div>

        <div class="flex items-center space-x-4">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
            Selected: {{ selectedSerials.length }}
          </span>

          <button type="button" class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 text-base font-medium" @click="printSelected">
            Print
          </button>
        </div>
      </div>
      <div class="flex flex-col">
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow">
              <table class="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-600">
                <thead class="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Select</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Serial No</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Item Code</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Item Name</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Label Name</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Label Size</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  <tr v-for="sn in serials" :key="sn.serial_no" 
                      :class="[
                        'select-none cursor-pointer transition-colors duration-150',
                        isSelected(sn) 
                          ? 'bg-blue-50 dark:bg-blue-900/20' 
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      ]"
                      @mouseenter="onRowMouseEnter(sn)"
                      @mousedown="onRowMouseDown(sn, $event)"
                      @mouseup="onRowMouseUp(sn, $event)">
                    <td :class="[
                          'p-4 text-sm text-gray-900 dark:text-white border-l-4',
                          isSelected(sn) ? 'border-blue-500' : 'border-transparent'
                        ]">
                      <div class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150" 
                           :class="isSelected(sn) 
                             ? 'bg-blue-600 border-blue-600 shadow-md' 
                             : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'">
                        <svg v-if="isSelected(sn)" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                        </svg>
                      </div>
                    </td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.serial_no }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.item_code }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.item_name }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.label_name || '-' }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.label_size || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'AppPackagingPrint',
  data() {
    return {
      quantity: 10,
      isLoading: false,
      qzConnected: false,
      printers: [],
      printerName: '',
      dpi: '203',
      messages: [],
      serials: [],
      selectedSerials: [],
      // ZPL offsets (applied to existing ^LS/^LT only)
      offsetLS: 0,
      offsetLT: 0,
      // Drag selection state
      isDragging: false,
      dragStartSerial: null,
      dragMode: null, // 'select' | 'deselect'
      mouseDownSerial: null // Track which serial was clicked
    };
  },
  computed: {
    connectBtnClass() {
      return [
        'px-4','py-2','text-white','rounded-lg','text-sm','font-medium',
        this.qzConnected ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
      ];
    },
    allSelected() {
      return this.serials.length > 0 && this.selectedSerials.length === this.serials.length;
    }
  },
  async mounted() {
    // 1) Wait until qz-tray.js is present
    await this.ensureQzReady().catch(() => this.addMessage('QZ library not found'));

    // 2) Register security hooks BEFORE connecting
    this.setupQzSecurity();

    // 3) Connect (only once)
    if (!window.qz || window.qz.websocket.isActive()) return;
    try {
      await this.connectQz();
    } catch (_) {
      this.addMessage('Auto-connect to QZ failed');
    }

    // Global mouseup to end drag even outside table
    window.addEventListener('mouseup', this.endDragSelection);
  },
  beforeUnmount() {
    // optional: clean disconnect
    if (window.qz && window.qz.websocket.isActive()) {
      window.qz.websocket.disconnect().catch(() => {});
    }

    window.removeEventListener('mouseup', this.endDragSelection);
  },
  methods: {
    isSelected(serial) {
      return this.selectedSerials.some(s => s.serial_no === serial.serial_no);
    },
    toggleSelection(serial) {
      const index = this.selectedSerials.findIndex(s => s.serial_no === serial.serial_no);
      if (index >= 0) {
        this.selectedSerials.splice(index, 1);
      } else {
        this.selectedSerials.push(serial);
      }
    },
    addMessage(m) { this.messages.push(m); },
    async ensureQzReady(timeoutMs = 5000) {
      const start = Date.now();
      return new Promise((resolve, reject) => {
        const t = setInterval(() => {
          if (window.qz && window.qz.security && window.qz.websocket) {
            clearInterval(t);
            resolve();
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(t);
            reject(new Error('QZ not loaded'));
          }
        }, 50);
      });
    },
    setupQzSecurity() {
      if (!window.qz || !window.qz.security) return;

      // Helpful while testing
      if (window.qz.api && window.qz.api.setLogLevel) {
        window.qz.api.setLogLevel('debug');
      }

      // Hand the LEAF certificate to QZ (resolver style)
      window.qz.security.setCertificatePromise(function (resolve, reject) {
        fetch('/qz/digital-certificate.txt', {
          cache: 'no-store',
          credentials: 'same-origin'
        })
          .then(r => (r.ok ? r.text() : Promise.reject(new Error('cert fetch failed'))))
          .then(txt => resolve(txt))
          .catch(err => reject(err));
      });

      // Sign with SHA-512 (server returns base64)
      window.qz.security.setSignatureAlgorithm('SHA512');
      window.qz.security.setSignaturePromise(function (toSign) {
        return function (resolve, reject) {
          fetch('/qz/sign', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ toSign })
          })
            .then(r => (r.ok ? r.text() : Promise.reject(new Error('sign failed'))))
            .then(sig => resolve(sig))
            .catch(err => reject(err));
        };
      });
    },
    async toggleQz() {
      if (!window.qz) { this.addMessage('QZ Tray library not loaded'); return; }
      if (this.qzConnected) { return this.disconnectQz(); }
      return this.connectQz();
    },
    async connectQz() {
      if (!window.qz) throw new Error('QZ not available');
      if (window.qz.websocket.isActive()) return;

      await window.qz.websocket.connect();
      this.qzConnected = true;
      this.addMessage('QZ Tray connected');
      await this.refreshPrinters();
    },
    async disconnectQz() {
      try {
        await window.qz.websocket.disconnect();
        this.qzConnected = false;
        this.addMessage('QZ Tray disconnected');
      } catch (e) {
        this.addMessage('Failed to disconnect QZ: ' + e);
      }
    },
    async refreshPrinters() {
      if (!this.qzConnected) { this.addMessage('Connect to QZ first'); return; }
      try {
        const printers = await window.qz.printers.find();
        this.printers = printers;
        this.printerName = printers[0] || '';
        this.autoSetDpiFromPrinter();
        this.addMessage('Printers loaded: ' + printers.length);
      } catch (e) {
        this.addMessage('Failed to load printers: ' + e);
      }
    },
    autoSetDpiFromPrinter() {
      const name = (this.printerName || '').toLowerCase();
      if (name.includes('300dpi') || name.includes('300 dpi') || name.includes('dpi300') || name.includes('zpl300')) {
        this.dpi = '300';
      } else if (name.includes('203dpi') || name.includes('203 dpi') || name.includes('dpi203') || name.includes('zpl203')) {
        this.dpi = '203';
      }
    },
    async createAndLoadSerials() {
        if (this.isLoading) return;
        this.isLoading = true;
        this.serials = [];
        this.selectedSerials = [];
        this.addMessage(`Creating ${this.quantity} serials...`);

        try {
            const res = await fetch('/erp/create-packaging-serials.json', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ qty: this.quantity })
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'Failed to create serials');
            }
            this.serials = data.serials || [];
            this.addMessage(`Successfully created and loaded ${this.serials.length} serials.`);
        } catch (e) {
            this.addMessage(`Error: ${e.message}`);
        } finally {
            this.isLoading = false;
        }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedSerials = [];
      } else {
        this.selectedSerials = [...this.serials];
      }
    },
    endDragSelection() {
      this.isDragging = false;
      this.dragStartSerial = null;
      this.dragMode = null;
      this.mouseDownSerial = null;
    },
    onRowMouseDown(serial, evt) {
      if (evt && evt.button !== 0) return; // left-click only
      this.mouseDownSerial = serial;
      this.dragStartSerial = serial;
      const isSelected = this.selectedSerials.some(s => s.serial_no === serial.serial_no);
      this.dragMode = isSelected ? 'deselect' : 'select';
      // Don't apply selection immediately - wait for mouseup or drag
    },
    onRowMouseUp(serial, evt) {
      if (evt && evt.button !== 0) return; // left-click only
      
      // If we didn't drag (mouseup on same serial as mousedown), toggle selection
      if (!this.isDragging && this.mouseDownSerial && this.mouseDownSerial.serial_no === serial.serial_no) {
        this.toggleSelection(serial);
      }
      
      this.mouseDownSerial = null;
    },
    onRowMouseEnter(serial) {
      // If we have a mousedown serial and we're entering a different serial, start dragging
      if (this.mouseDownSerial && this.mouseDownSerial.serial_no !== serial.serial_no && !this.isDragging) {
        this.isDragging = true;
        // Apply the initial selection to the starting row
        if (this.dragMode === 'select') {
          const isSelected = this.selectedSerials.some(s => s.serial_no === this.dragStartSerial.serial_no);
          if (!isSelected) this.selectedSerials.push(this.dragStartSerial);
        } else {
          const idx = this.selectedSerials.findIndex(s => s.serial_no === this.dragStartSerial.serial_no);
          if (idx >= 0) this.selectedSerials.splice(idx, 1);
        }
      }

      if (!this.isDragging || !this.dragStartSerial || !this.dragMode) return;
      
      // Find range between drag start and current serial
      const startIndex = this.serials.findIndex(s => s.serial_no === this.dragStartSerial.serial_no);
      const endIndex = this.serials.findIndex(s => s.serial_no === serial.serial_no);
      
      if (startIndex >= 0 && endIndex >= 0) {
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);
        // Apply selection/deselection to range
        for (let i = minIndex; i <= maxIndex; i++) {
          const targetSerial = this.serials[i];
          const index = this.selectedSerials.findIndex(s => s.serial_no === targetSerial.serial_no);
          if (this.dragMode === 'select' && index < 0) {
            this.selectedSerials.push(targetSerial);
          } else if (this.dragMode === 'deselect' && index >= 0) {
            this.selectedSerials.splice(index, 1);
          }
        }
      }
    },
    async fetchZplTemplate(itemCode, dpi) {
      const url = new URL('/erp/zpl-template/', window.location.origin);
      url.searchParams.set('item_code', itemCode);
      url.searchParams.set('dpi', dpi);
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      const data = await res.json();
      if (!res.ok) {
        const msg = data && data.error ? data.error : 'Failed to fetch ZPL';
        throw new Error(msg);
      }
      return data;
    },
    fillZpl(template, payload) {
      let out = template;
      Object.entries(payload).forEach(([key, value]) => {
        const safe = value == null ? '' : String(value);
        const escaped = String(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        out = out.replace(new RegExp('\\{' + escaped + '\\}', 'gi'), () => safe);
      });
      return out;
    },
    chunk(array, size) {
      const chunks = [];
      for (let i = 0; i < array.length; i += size) chunks.push(array.slice(i, i + size));
      return chunks;
    },
    clamp(n, min, max) {
      return Math.min(Math.max(n, min), max);
    },
    adjustZpl(zpl) {
      let out = zpl;
      // Adjust ^LS (horizontal) if present and offset non-zero
      if (this.offsetLS) {
        out = out.replace(/\^LS(-?\d+)/g, (match, val) => {
          const base = parseInt(val, 10);
          if (Number.isNaN(base)) return match;
          const adjusted = this.clamp(base + (parseInt(this.offsetLS, 10) || 0), -9999, 9999);
          return `^LS${adjusted}`;
        });
      }
      // Adjust ^LT (vertical) if present and offset non-zero
      if (this.offsetLT) {
        out = out.replace(/\^LT(-?\d+)/g, (match, val) => {
          const base = parseInt(val, 10);
          if (Number.isNaN(base)) return match;
          const adjusted = this.clamp(base + (parseInt(this.offsetLT, 10) || 0), -120, 120);
          return `^LT${adjusted}`;
        });
      }
      return out;
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async printSelected() {
      if (!this.qzConnected) { this.addMessage('Connect to QZ first'); return; }
      if (!this.selectedSerials.length) { this.addMessage('No serials selected'); return; }
      
      const actuallyPrinted = [];
      let printedLabelCount = 0; // count physical labels sent to printer
      
      try {
        const itemCode = 'QLINK';
        const tplResp = await this.fetchZplTemplate(itemCode, this.dpi);
        const template = tplResp.zpl;
        const labelSize = (tplResp.label_size || '').toLowerCase();
        const pauseInterval = parseInt(tplResp.pause_interval || 0, 10);

        const cfg = window.qz.configs.create(this.printerName);
        cfg.reconfigure({ 'raw.encoding': 'UTF-8' });

        if (labelSize === 'small') {
          for (const quad of this.chunk(this.selectedSerials, 4)) {
            const payload = {
              SERIAL1: quad[0]?.serial_no || '',
              SERIAL2: quad[1]?.serial_no || '',
              SERIAL3: quad[2]?.serial_no || '',
              SERIAL4: quad[3]?.serial_no || '',
              ITEM_CODE: itemCode,
              IMEI: '',
              PCN_REVISION: '',
            };
            const zpl = this.adjustZpl(this.fillZpl(template, payload));
            await window.qz.print(cfg, [{ type: 'raw', format: 'plain', data: zpl }]);
            quad.forEach(s => s && s.serial_no && actuallyPrinted.push(s.serial_no));
            
            printedLabelCount += 1;
            if (pauseInterval > 0 && printedLabelCount % pauseInterval === 0) {
              this.addMessage(`Cooling printer for 4s after ${pauseInterval} labels...`);
              await this.sleep(2000);
            }
          }
        } else {
          for (const s of this.selectedSerials) {
            const payload = { SERIAL: s.serial_no, ITEM_NAME: s.item_name, IMEI: '', PCN_REVISION: '' };
            const zpl = this.adjustZpl(this.fillZpl(template, payload));
            await window.qz.print(cfg, [{ type: 'raw', format: 'plain', data: zpl }]);
            actuallyPrinted.push(s.serial_no);
            
            printedLabelCount += 1;
            if (pauseInterval > 0 && printedLabelCount % pauseInterval === 0) {
              this.addMessage(`Cooling printer for 4s after ${pauseInterval} labels...`);
              await this.sleep(2000);
            }
          }
        }
        this.addMessage(`Printed ${this.selectedSerials.length} for ${itemCode}`);
      } catch (e) {
        this.addMessage(`Print failed for QLINK: ${e}`);
      }
      
    }
  }
}
</script>
